package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavController
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.FeaturePollOption
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.BackIconButton
import com.shadowings.gero.presentation.component.FeaturePollForm
import com.shadowings.gero.presentation.component.quiz.AlynFeedbackBox
import com.shadowings.gero.presentation.viewmodel.FeaturePollViewModel
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.feature_poll_subtitle
import gero.composeapp.generated.resources.feature_poll_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun FeaturePollScreen(
    navController: NavController,
    viewModel: FeaturePollViewModel = koinViewModel(),
) {
    val userData by viewModel.userData.collectAsStateWithLifecycle()
    var selectedOption by remember { mutableStateOf<FeaturePollOption?>(null) }
    var freeText by remember { mutableStateOf("") }

    FeaturePollScreenContent(
        userData = userData,
        selectedOption = selectedOption,
        onOptionSelected = { selectedOption = it },
        freeText = freeText,
        onFreeTextChange = { freeText = it },
        onSubmit = {
            selectedOption?.let {
                viewModel.submit(it, freeText)
                navController.popBackStack()
            }
        },
        onBack = { navController.popBackStack() }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun FeaturePollScreenContent(
    userData: UserData,
    selectedOption: FeaturePollOption?,
    onOptionSelected: (FeaturePollOption) -> Unit,
    freeText: String,
    onFreeTextChange: (String) -> Unit,
    onSubmit: () -> Unit,
    onBack: () -> Unit
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = stringResource(Res.string.feature_poll_title),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                navigationIcon = {
                    BackIconButton(onBackClick = onBack, testTagId = "feature_poll_btn_back")
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.background
                )
            )
        },
        containerColor = MaterialTheme.colorScheme.background
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(16.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            AlynFeedbackBox(
                face = Faces.Doubt,
                text = stringResource(Res.string.feature_poll_subtitle),
                userData = userData
            )

            FeaturePollForm(
                selectedOption = selectedOption,
                onOptionSelected = onOptionSelected,
                freeText = freeText,
                onFreeTextChange = onFreeTextChange,
                onSubmit = onSubmit
            )
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun FeaturePollScreenPreview() {
    com.shadowings.gero.presentation.ui.AppTheme {
        FeaturePollScreenContent(
            userData = UserData(),
            selectedOption = null,
            onOptionSelected = {},
            freeText = "",
            onFreeTextChange = {},
            onSubmit = {},
            onBack = {}
        )
    }
}
